import React, { useMemo, useState } from 'react';
import { Offcanvas, ListGroup, Button, InputGroup, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import { IconCheck, IconX, IconArrowRight } from '@tabler/icons-react';

// Impor Tipe dan Hook V4
import { UserV4 } from '../schemas/UserV4Schema';
import { useGetAllPropertiesV4 } from '../hooks/useGetAllPropertiesV4';
import ErrorState from './ErrorState';

// *** PERUBAHAN: Impor semua hook yang relevan ***
import { useAttachPropertyV4 } from '../hooks/useAttachPropertyV4';
import { useDetachPropertyV4 } from '../hooks/useDetachPropertyV4';
import { useSyncPropertyV4 } from '../hooks/useSyncPropertyV4';


interface AssignPropertyDialogV4Props {
    show: boolean;
    onClose: () => void;
    user: UserV4 | null;
}

type SelectOption = {
    value: string;
    label: string;
};

const AssignPropertyDialogV4: React.FC<AssignPropertyDialogV4Props> = ({ show, onClose, user }) => {
    const [propertyToAssign, setPropertyToAssign] = useState<SelectOption | null>(null);

    const { data: allPropertiesResponse, isLoading: isLoadingProperties, isError: isErrorProperties, error: propertiesError, refetch: refetchProperties } = useGetAllPropertiesV4();

    // *** PERUBAHAN: Inisialisasi ketiga hook mutasi ***
    const { mutate: attachProperty, isPending: isAttaching } = useAttachPropertyV4();
    const { mutate: detachProperty, isPending: isDetaching } = useDetachPropertyV4();
    const { mutate: syncProperties, isPending: isSyncing } = useSyncPropertyV4();

    // Gabungkan semua status 'pending' untuk menonaktifkan UI
    const isProcessing = isAttaching || isDetaching || isSyncing;

    const availableProperties = useMemo((): SelectOption[] => {
        if (!allPropertiesResponse || !user) return [];
        const assignedPropertyIds = new Set(user.properties?.map(p => p.id));
        return allPropertiesResponse.data
            .filter(property => !assignedPropertyIds.has(property.id))
            .map(property => ({ value: property.id, label: property.name, }));
    }, [allPropertiesResponse, user]);

    const handleClose = () => {
        setPropertyToAssign(null);
        onClose();
    };

    // *** PERUBAHAN: Kembalikan logika 'handleAttach' ke versi aslinya ***
    const handleAttach = () => {
        if (!propertyToAssign || !user) return;
        const toastId = 'attach-property-v4';
        toast.loading('Assigning property...', { id: toastId });

        attachProperty({ userId: user.id, propertyId: propertyToAssign.value }, {
            onSuccess: () => {
                toast.success('Property assigned successfully.', { id: toastId });
                setPropertyToAssign(null);
            },
            onError: (err) => {
                const errorMessage = (err as any)?.response?.data?.message || 'Failed to assign property.';
                toast.error(errorMessage, { id: toastId });
            }
        });
    };

    // *** PERUBAHAN: Kembalikan logika 'handleDetach' ke versi aslinya ***
    const handleDetach = (propertyId: string) => {
        if (!user) return;
        const toastId = 'detach-property-v4';
        toast.loading('Detaching property...', { id: toastId });

        detachProperty({ userId: user.id, propertyId: propertyId }, {
            onSuccess: () => {
                toast.success('Property detached successfully.', { id: toastId });
            },
            onError: (err) => {
                const errorMessage = (err as any)?.response?.data?.message || 'Failed to detach property.';
                toast.error(errorMessage, { id: toastId });
            }
        });
    };

    // Logika 'handleAssignAll' tetap menggunakan 'useSyncPropertyV4'
    const handleAssignAll = () => {
        if (!allPropertiesResponse || !user) return;
        const toastId = 'sync-all-properties-v4';
        toast.loading('Assigning all properties...', { id: toastId });

        const allPropertyIds = allPropertiesResponse.data.map(p => p.id);

        syncProperties({ user_ids: [user.id], property_ids: allPropertyIds }, {
            onSuccess: () => {
                toast.success('All properties assigned successfully.', { id: toastId });
            },
            onError: (err) => {
                const errorMessage = (err as any)?.response?.data?.message || 'Failed to assign all properties.';
                toast.error(errorMessage, { id: toastId });
            }
        });
    };

    const renderBody = () => {
        if (isLoadingProperties) {
            return <div className="d-flex justify-content-center mt-5"><Spinner animation="border" /></div>;
        }
        if (isErrorProperties) {
            return <ErrorState error={propertiesError} onRetry={refetchProperties} />;
        }

        return (
            <fieldset disabled={isProcessing}>
                <div className="mb-4">
                    <label className="form-label fw-medium">Assign New Property</label>
                    <InputGroup>
                        <Select
                            className="flex-grow-1"
                            classNamePrefix="react-select"
                            options={availableProperties}
                            placeholder="Select a property to assign..."
                            value={propertyToAssign}
                            onChange={setPropertyToAssign}
                            isClearable
                        />
                        <Button variant="outline-success" onClick={handleAttach} disabled={!propertyToAssign || isProcessing}>
                            <IconCheck size={18} />
                        </Button>
                    </InputGroup>
                </div>

                <div className="mb-4">
                    <label className="form-label fw-medium">Bulk Action</label>
                    <div className="d-grid">
                        <Button variant="outline-primary" onClick={handleAssignAll} disabled={isProcessing}>
                            Assign All Properties
                            <IconArrowRight size={18} className="ms-2" />
                        </Button>
                    </div>
                </div>

                <div>
                    <label className="form-label fw-medium">Assigned Properties</label>
                    <div className="border rounded" style={{ minHeight: '100px', maxHeight: '50vh', overflowY: 'auto' }}>
                        {user?.properties && user.properties.length > 0 ? (
                            <ListGroup variant="flush">
                                {user.properties.map(property => (
                                    <ListGroup.Item key={property.id} className="d-flex justify-content-between align-items-center">
                                        <span>{property.name}</span>
                                        <Button variant="light" size="sm" onClick={() => handleDetach(property.id)} disabled={isProcessing} title={`Detach ${property.name}`}>
                                            <IconX size={16} className="text-danger" />
                                        </Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <div className="d-flex align-items-center justify-content-center p-4 text-muted">
                                No properties assigned to this user.
                            </div>
                        )}
                    </div>
                </div>
            </fieldset>
        );
    };

    return (
        <Offcanvas show={show} onHide={handleClose} placement='end' backdrop="static" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Manage Properties for {user?.name}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {renderBody()}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AssignPropertyDialogV4;